import React, { useRef, useEffect } from "react";
import { RawSpGetSearchQuantityBreak } from '../../interfaces/rawSpGetSearchQuantityBreak';
import { ItemIdentifier } from '../../interfaces/itemIdentifier';
import { IndividualUnitDataModel } from '../../interfaces/individualUnitDataModel';
import { NavitorPrice } from '../../interfaces/navitorPrice';
import CloseIcon from "@material-ui/icons/Close";
import { PurchaseProcess } from "../../constants/enums";

type quantityBreakProps = {
    quantityBreaks: Array<RawSpGetSearchQuantityBreak>;
    itemIdentifier: ItemIdentifier;
    unitPrice: number;
    individualUnitData?: IndividualUnitDataModel;
    selectedQuantity: number;
    displayUnitPricing: boolean;
    onClick: Function;
    couponCustomerLimitMessage: string;
    couponQtyLimitMessage: string;
    baseOncePerCustomer: boolean,
    baseQtyLimit: number,
    qtyTier?: number[];
    navitorPrice?: NavitorPrice;
    purchaseProcess?: number;
};

const romanNumeralOne = 8560;

export const QuantityBreak = ({ ...props }: quantityBreakProps) => {
    const { navitorPrice, purchaseProcess } = props || {};
    const { lotQty, endQtyPrice, minQty } = navitorPrice || {};
    const isNavInkProduct = purchaseProcess === PurchaseProcess.NavInk;

    const showUomHeader = navitorPrice && isNavInkProduct && !lotQty;
    const isLotQtyItem = navitorPrice && isNavInkProduct && lotQty;
    let hasEQPNotation = false;

    const checkQty = (qty: number): string => {
        if (navitorPrice && isNavInkProduct && endQtyPrice === 1 && minQty === qty) {
            hasEQPNotation = true;
            return '*';
        }
        return ''
    }

    const focusClose: any = useRef();
    useEffect(() => {
        if (focusClose.current) {
            focusClose.current.focus();
        }
    }, [focusClose]);

    let qtyBreakRows: Array<JSX.Element> = [];
    let qtyBreakFootnotes: Array<JSX.Element> = [];
    let updatedQtyBreaks: RawSpGetSearchQuantityBreak[] = props.quantityBreaks;
    let limitQtyCount = 0;
    let oncePerCustomerShown = false;

    if (props.baseOncePerCustomer && !oncePerCustomerShown) {
        qtyBreakFootnotes.push(<div className="footnote" key={"footnote_" + Math.random()}>{String.fromCharCode(romanNumeralOne) + " " + props.couponCustomerLimitMessage}</div>);
        oncePerCustomerShown = true;
    }

    updatedQtyBreaks.forEach(qb => {
        if (qb.oncePerCustomer && !oncePerCustomerShown) {
            qtyBreakFootnotes.push(<div className="footnote" key={"footnote_" + Math.random()}>{String.fromCharCode(romanNumeralOne) + " " + props.couponCustomerLimitMessage}</div>);
            oncePerCustomerShown = true;
        }
    });

    if (props.baseQtyLimit > 0) {
        limitQtyCount++;
        const footNoteString = String.fromCharCode(romanNumeralOne + limitQtyCount);
        qtyBreakFootnotes.push(<div className="footnote" key={"footnote_" + Math.random()}>{footNoteString + " " + props.couponQtyLimitMessage.replace("[LimitQuantity]", props.baseQtyLimit.toString())}</div>);    }
    const updateQtyBreakRows = (
        item: RawSpGetSearchQuantityBreak,
        i: number) => {
        const updatedQB = props.qtyTier && props.qtyTier.length > 0 ? props.qtyTier : props.quantityBreaks;

        qtyBreakRows.push(
            <tr className='table-row-list' key={"row" + i}>
                <td scope="col">
                    <span className="highlight"></span>
                    {i !== updatedQB.length - 1 ?
                        `${item.quantity}${checkQty(item.quantity)}`
                        :
                        `${item.quantity}${isLotQtyItem ? '' : '+'}`
                    }
                </td>
                <td>
                    <div>
                        <div
                            className={
                                props.displayUnitPricing && props.individualUnitData ? "spacing-extra" : "spacing"
                            }
                        >
                            ${parseFloat(item.price).toFixed(2)}
                            {lotQty ? null : (
                                <>
                                    {item.oncePerCustomer && (
                                        <span className='footnote-indicator'>
                                            {String.fromCharCode(romanNumeralOne)}
                                        </span>
                                    )}
                                    {item.footNoteString && (
                                        <span className='footnote-indicator'>
                                            {item.footNoteString}
                                        </span>
                                    )}
                                    {!showUomHeader ? `${' '}/${props.itemIdentifier.unitId}` : ''}
                                </>
                            )}
                        </div>
                        {!isNavInkProduct && (!props.displayUnitPricing || !props.individualUnitData) ? (
                            <div className="saving-holder">
                                (
                                <span className="saving-label">
                                    Save ${(props.unitPrice - parseFloat(item.price)).toFixed(2)}
                                </span>{" "}
                                /{props.itemIdentifier.unitId})
                            </div>
                        ) : null}
                    </div>
                </td>
                {props.displayUnitPricing && props.individualUnitData && props.individualUnitData.indivUnitCount !== 0 ? (
                    <td>
                        <div>
                            <div className="spacing">
                                <span className="font-weight-bold">
                                    ${(parseFloat(item.price) / (!showUomHeader ? props.individualUnitData.indivUnitCount : 1)).toFixed(2)}
                                    {item.oncePerCustomer && <span className="footnote-indicator">{String.fromCharCode(romanNumeralOne)}</span>}
                                    {item.footNoteString && <span className="footnote-indicator">{item.footNoteString}</span>}
                                </span>{" "}
                                / {props.individualUnitData.indivUnitLabel}
                            </div>
                            {!isNavInkProduct && <div className="saving-holder">
                                (
                                <span className="saving-label">
                                    Save ${((props.unitPrice / props.individualUnitData.indivUnitCount) - (parseFloat(item.price) / props.individualUnitData.indivUnitCount)).toFixed(2)}
                                </span>{" "}
                                /{props.individualUnitData.indivUnitLabel})
                            </div>}
                        </div>
                    </td>
                ) : (
                    <td></td>
                )}
            </tr>
        );
    }

    updatedQtyBreaks.forEach(qb => {
        if (qb.limitQty > 0) {
            limitQtyCount++;
            qb.footNoteString = String.fromCharCode(romanNumeralOne + limitQtyCount);
        }
    });

    if (props.qtyTier && props.qtyTier.length > 0) {
        const itemQtyBreaks = props.quantityBreaks.filter((qtyBreak) => {
            return (qtyBreak.itemNumber === props.itemIdentifier.id
                && qtyBreak.unitId === props.itemIdentifier.unitId);
        });
        const maxQtyItem = itemQtyBreaks.reduce((maxQtyItem, currentItem) => {
            return currentItem.quantity > maxQtyItem.quantity ? currentItem : maxQtyItem;
        }, itemQtyBreaks[0]);

        updatedQtyBreaks = updatedQtyBreaks.filter(qb => props.qtyTier?.includes(qb.quantity));

        props.qtyTier.forEach(currentQtyTier => {
            const isQtyTierLessThanAll = currentQtyTier < props.quantityBreaks[0].quantity;
            const isInRange = props.quantityBreaks.some((qb, i) =>
                currentQtyTier > qb.quantity &&
                (i !== props.quantityBreaks.length - 1 && currentQtyTier < props.quantityBreaks[i + 1]?.quantity));
            if (isQtyTierLessThanAll) {
                const qbPrice = parseFloat(props.unitPrice.toString()).toFixed(2);
                const currentQtyBreak = updatedQtyBreaks[0];
                updatedQtyBreaks.push({
                    ...currentQtyBreak,
                    limitQty: 0,
                    oncePerCustomer: false,
                    price: qbPrice,
                    quantity: currentQtyTier,
                    footNoteString: currentQtyBreak?.footNoteString
                });
            } else if (isInRange) {
                const relevantQtyBreak: any = props.quantityBreaks.find((qb, i) =>
                    currentQtyTier >= qb.quantity &&
                    (i !== props.quantityBreaks.length - 1 && currentQtyTier < props.quantityBreaks[i + 1]?.quantity));
                const qbPrice = parseFloat(relevantQtyBreak.price).toFixed(2);
                updatedQtyBreaks.push({
                    ...relevantQtyBreak,
                    price: qbPrice,
                    quantity: currentQtyTier,
                    footNoteString: relevantQtyBreak?.footNoteString
                });
            } else if (currentQtyTier >= maxQtyItem.quantity && !updatedQtyBreaks.some(qb => qb.quantity === currentQtyTier)) {
                const qbPrice = parseFloat(maxQtyItem.price).toFixed(2);
                updatedQtyBreaks.push({
                    ...maxQtyItem,
                    price: qbPrice,
                    quantity: currentQtyTier,
                    footNoteString: maxQtyItem?.footNoteString
                });
            }
        });

        updatedQtyBreaks.sort((a, b) => a.quantity - b.quantity);
    }

    updatedQtyBreaks.forEach((item: RawSpGetSearchQuantityBreak, i: number) => {
        item.footNoteString && qtyBreakFootnotes.push(<div className="footnote" key={"footnote_" + Math.random()}>{item.footNoteString + " " + props.couponQtyLimitMessage.replace("[LimitQuantity]", item.limitQty.toString())}</div>);
        updateQtyBreakRows(item, i);
    });


    return (
        <div className="quantity-break-overlay">
            <div className="quantity-break">
                <div className={"closeIcon"} tabIndex={0} onKeyPress={(e) => props.onClick(e)} onClick={(e) => props.onClick(e)} ref={focusClose} >
                    <CloseIcon />
                </div>
                <div className={`table-block ${qtyBreakFootnotes.length > 0 ? 'table-footnotes' : ''}`}>
                    <table className="table">
                        <thead>
                            <tr className="table-row-header">
                                <th>QTY</th>
                                <th>{`Price ${showUomHeader ? `${' '}/ ${props.itemIdentifier.unitId}` : ''}`}</th>
                                {props.displayUnitPricing && props.individualUnitData && props.individualUnitData.indivUnitCount !== 0 ? <th>Unit Price</th> : <td></td>}
                            </tr>
                        </thead>
                        <tbody>
                            <>{qtyBreakRows}</>
                        </tbody>
                    </table>
                </div>
                {
                    qtyBreakFootnotes
                }
                {hasEQPNotation && <div className="eqp-notation">* Minimum Quantity</div>}
            </div>
        </div>
    );
};

QuantityBreak.defaultProps = {
    baseQtyLimit: 0,
    couponCustomerLimitMessage: `Discount valid for 1 order per account`,
    couponQtyLimitMessage: `Discount valid on orders of [LimitQuantity] or less.`
};